import React, {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react'

import {
  PromptGroup,
  promptType,
  TempoType,
  ToneType,
  VoiceGroupFactor,
  VoiceTagType,
} from 'service/list'
import classNames from 'classnames'
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import { contentVideoFormValues } from 'utils/type'

import { Me } from 'service/user'
import { isMobile } from 'react-device-detect'

import TooltipIcon from 'components/TooltipIcon'
import { CircularCheckbox } from 'components/CircularCheckbox'
import { TypeCastVoicePlayer } from 'pages/product-video-editor/components/video/voice/TypeCastVoicePlayer'

import { SpeedPlayer } from 'pages/product-video-editor/components/video/voice/SpeedPlayer'
import { SelectionModal } from 'components/layout/SelectionModal'
import { UserVoiceList } from './UserVoiceList'

interface AiScriptDisplayProps {
  watch: UseFormWatch<contentVideoFormValues>
  setValue: UseFormSetValue<contentVideoFormValues>
  control: Control<contentVideoFormValues, any>
  userVoiceList: VoiceGroupFactor[] | undefined
  userVoiceRefetch: any
  toneList: ToneType[] | undefined
  tempoList: TempoType[] | undefined
  voiceList: VoiceGroupFactor[] | undefined
  userInfo: Me
  setPromptStatus: Dispatch<SetStateAction<boolean[]>>
  setVoicePlaying: Dispatch<SetStateAction<boolean[]>>
  setTempoPlaying: Dispatch<SetStateAction<boolean[]>>
  setToneTooltip: Dispatch<SetStateAction<boolean[]>>
  promptStatus: boolean[]
  voicePlaying: boolean[]
  toneTooltip: boolean[]
  tempoPlaying: boolean[]
  voiceTags: VoiceTagType | undefined
}

export const AiScriptDisplay = ({
  watch,
  setValue,
  control,
  toneList,
  tempoList,
  userInfo,
  setPromptStatus,
  promptStatus,
  setVoicePlaying,
  voicePlaying,
  voiceList,
  voiceTags,
  setToneTooltip,
  toneTooltip,
  tempoPlaying,
  setTempoPlaying,
  userVoiceList,
  userVoiceRefetch,
}: PropsWithChildren<AiScriptDisplayProps>) => {
  const voiceRefs = useRef<(HTMLAudioElement | null)[]>([])
  const tempoRefs = useRef<(HTMLAudioElement | null)[]>([])
  const tonRefs = useRef<(HTMLOrSVGElement | null)[]>([])
  const [isModalOpen, setModalOpen] = useState<boolean>(false)
  const [isGenderSelectBoxOpen, setGenderSelectBoxOpen] =
    useState<boolean>(false)
  const [isAgeSelectBoxOpen, setAgeSelectBoxOpen] = useState<boolean>(false)
  const [isMobilePromptSelectBoxOpen, setMobilePromptSelectBoxOpen] =
    useState<boolean>(false)

  const [genderWords, setGenderWords] = useState<string[]>(
    voiceTags?.genders ?? ['남성', '여성'],
  )

  const [ageWords, setAgeWords] = useState<string[]>(
    voiceTags?.ages ?? ['청년', '중년 이상', '청소년'],
  )

  const [tempoName, setTempoName] = useState<string>('')
  const [selectedPrompt, setSelectedPrompt] = useState<
    PromptGroup | { planName: string; introduction: string }
  >({
    planName: '',
    introduction: '',
  })
  const [toneName, setToneName] = useState<string>('')
  const [voiceName, setVoiceName] = useState<string>('')
  const [genderValue, setGenderValue] = useState<string>('')
  const [ageValue, setAgeValue] = useState<string>('')
  const [voiceListValue, setVoiceListValue] = useState<VoiceGroupFactor[] | []>(
    [],
  )

  const handleListModalOpen = () => {
    setModalOpen((prev) => !prev)
  }

  const handleVoicePlayButtonClick = (status: boolean, index: number) => {
    const updatedPlayingState = voicePlaying.map((value, idx) =>
      idx === index ? status : false,
    )
    setVoicePlaying(updatedPlayingState)
    if (voiceRefs.current[index]) {
      if (status) {
        voiceRefs.current[index]?.play()
      } else {
        voiceRefs.current[index]?.pause()
      }
    }
  }

  const handleTempoPlayButtonClick = (status: boolean, index: number) => {
    const updatedPlayingState = tempoPlaying.map((value, idx) =>
      idx === index ? status : false,
    )
    setTempoPlaying(updatedPlayingState)
    if (tempoRefs.current[index]) {
      if (status) {
        tempoRefs.current[index]?.play()
      } else {
        tempoRefs.current[index]?.pause()
      }
    }
  }
  const handleGenderChange = (gender: string) => {
    setGenderWords(
      (prev) =>
        prev.includes(gender)
          ? prev.filter((item) => item !== gender) // 체크 해제
          : [...prev, gender], // 체크 선택
    )
  }
  const handleAgeChange = (age: string) => {
    setAgeWords(
      (prev) =>
        prev.includes(age)
          ? prev.filter((item) => item !== age) // 체크 해제
          : [...prev, age], // 체크 선택
    )
  }

  const handleMouseOver = (index: number) => {
    const updatedToneTooltipState = toneTooltip.map((value, idx) =>
      idx === index ? true : value,
    )

    setToneTooltip(updatedToneTooltipState)
  }

  const handleMouseLeave = (index: number) => {
    const updatedToneTooltipState = toneTooltip.map((value, idx) =>
      idx === index ? false : value,
    )
    setToneTooltip(updatedToneTooltipState)
  }

  useEffect(() => {
    voiceRefs.current.forEach((audioRef, index) => {
      if (audioRef && !voicePlaying[index]) {
        audioRef.pause()
        audioRef.currentTime = 0
      }
    })
  }, [voicePlaying])

  useEffect(() => {
    tempoRefs.current.forEach((audioRef, index) => {
      if (audioRef && !tempoPlaying[index]) {
        audioRef.pause()
        audioRef.currentTime = 0
      }
    })
  }, [tempoPlaying])

  useEffect(() => {
    if (voiceList) {
      let filter = voiceList

      // 1차: 성별 필터링
      if (genderWords.length > 0) {
        filter = filter.filter((el) => genderWords.includes(el?.gender ?? ''))
      } else {
        filter = []
      }

      // 2차: 연령 필터링
      if (ageWords.length > 0) {
        filter = filter.filter((el) => ageWords.includes(el.age ?? ''))
      } else {
        filter = []
      }

      // 상태 업데이트
      const prevVoiceId = watch('voiceId')

      setVoiceListValue(filter)
      setVoicePlaying(new Array(filter.length).fill(false))
    }
  }, [genderWords, ageWords, voiceList])

  useEffect(() => {
    if (isModalOpen) {
      document.body.classList.add('overflow-hidden') // Tailwind 유틸리티 클래스 사용
    } else {
      document.body.classList.remove('overflow-hidden')
    }
  }, [isModalOpen])

  useEffect(() => {}, [])

  useEffect(() => {
    const aiToneTypeId = watch('aiToneTypeId')
    const tempoId = watch('tempoId')
    const selectedVoiceId = watch('voiceId')
    const selectedUserVoiceId = watch('userVoiceId')

    if (voiceList && selectedVoiceId !== 0) {
      //voice아이디를 가지고 list에서 찾아서 gender와 age를 셋팅해야함

      const [voice] = voiceList.filter((el) => el.id === selectedVoiceId)
      if (voice) {
        setVoiceName(voice?.name)
        setAgeValue(voice.age ?? '')
        setGenderValue(voice.gender ?? '')
      }
    }
    if (userVoiceList && selectedUserVoiceId !== 0) {
      //voice아이디를 가지고 list에서 찾아서 gender와 age를 셋팅해야함

      const [voice] = userVoiceList.filter(
        (el) => el.id === selectedUserVoiceId,
      )
      if (voice) {
        setVoiceName(voice?.name)
        setAgeValue(voice.age ?? '')
        setGenderValue(voice.gender ?? '')
      }
    }
    if (toneList && aiToneTypeId) {
      const selectedToneId = watch('aiToneTypeId')
      const [tone] = toneList.filter((el) => el.id === selectedToneId)
      setToneName(tone?.name)
    }
    if (tempoList && tempoId) {
      const selectedTempoId = watch('tempoId')
      const [tempo] = tempoList.filter((el) => el.id === selectedTempoId)
      setTempoName(tempo?.name)
    }
  }, [
    toneList,
    tempoList,
    watch('aiToneTypeId'),
    watch('tempoId'),
    voiceList,
    watch('voiceId'),
    watch('userVoiceId'),
  ])

  return (
    <>
      <div className="bgm mb-10">
        <div className="flex flex-col justify-between gap-3 md:flex-row md:items-center">
          <p className="mb-4 w-[92px] shrink-0 text-[18px] font-bold md:mb-0">
            AI 생성 <br /> 목소리
          </p>
          <p className="flex w-full items-center justify-between rounded-[10px] bg-[#F6F8FB] px-4 py-5 font-bold md:h-[61px] md:w-[630px]">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:gap-5">
              <p className="flex items-center gap-1 text-[14px]">
                <span className="flex-shrink-0 rounded-[4px] bg-[#D9DEE8] px-1 py-[2px] text-[#8F929B]">
                  속도
                </span>
                <span className="flex-shrink-0">{tempoName}</span>
              </p>
              <p className="flex items-center gap-1 text-[14px]">
                <span className="flex-shrink-0 rounded-[4px] bg-[#D9DEE8] px-1 py-[2px] text-[#8F929B]">
                  말투
                </span>
                <span className="">{toneName}</span>
              </p>{' '}
              <p className="flex items-center gap-1 text-[14px]">
                <span className="flex-shrink-0 rounded-[4px] bg-[#D9DEE8] px-1 py-[2px] text-[#8F929B]">
                  목소리
                </span>
                <div className="flex items-center">
                  <span
                    className="flex-shrink-0 rounded-[4px] px-1 py-[2px] text-[11px] font-bold text-white"
                    style={{
                      background: `linear-gradient(to right,#6095FF,#00CBB3)`,
                    }}
                  >
                    UPDATE
                  </span>
                </div>
                <span className="flex-shrink-0">{voiceName}</span>
              </p>
            </div>
            <button
              className="flex-shrink-0 rounded-[4px] bg-[#2E343D] px-2 py-[6px] text-[14px] text-[white]"
              onClick={handleListModalOpen}
              type="button"
            >
              선택 {'>'}
            </button>
          </p>
        </div>
      </div>

      <SelectionModal isModal={isModalOpen}>
        <div
          className={classNames('', {
            'flex h-auto w-[816px] flex-col gap-8 rounded-t-[20px] bg-white px-8 pb-28 pt-8':
              !isMobile,
            'flex w-full flex-col gap-8 bg-white px-4 pt-10': isMobile,
          })}
        >
          <div className="flex flex-col gap-4">
            <p className="flex flex-col items-center gap-4 md:items-start">
              <span className="text-left text-[24px] font-bold">
                말투 & 속도 선택
              </span>
              <span>말투와 속도를 선택한 후 목소리를 선택하세요!</span>
            </p>
          </div>
          <div className="flex flex-col gap-8 pb-40 md:pb-0">
            <div className="ai-prompt md:min-h-auto flex flex-col items-start">
              <p className="mb-[16px] text-[18px] font-bold">나레이션 말투</p>
              <div className="grid grid-cols-1 gap-x-[53.67px] gap-y-[16px] md:grid-cols-2">
                {toneList?.map((tone, index) => {
                  return (
                    <>
                      <div
                        className="flex w-full justify-between md:items-center"
                        key={`tone_${tone.id}_${index}`}
                      >
                        <div className="flex h-full items-center gap-1">
                          <Controller
                            render={({ field }) => (
                              <CircularCheckbox
                                {...field}
                                id={index}
                                checked={watch('aiToneTypeId') === tone.id}
                                onChange={() => {
                                  setValue('aiToneTypeId', tone.id)
                                  setToneName(tone.name)
                                }}
                                label={tone.name}
                              />
                            )}
                            name="aiToneTypeId"
                            control={control}
                          />

                          <TooltipIcon
                            tone={tone}
                            index={index}
                            tonRefs={tonRefs}
                            toneTooltip={toneTooltip}
                            handleMouseOver={handleMouseOver}
                            handleMouseLeave={handleMouseLeave}
                          />
                          {/* {new Array(tone?.cashRate).fill(0).map((el) => (
                              <>
                                {' '}
                                <img
                                  src="assets/img/cash.png"
                                  alt="cash"
                                  className="w-[11px] h-[13px] ml-[2px] mt-[2px]"
                                />
                              </>
                            ))} */}
                        </div>
                      </div>
                    </>
                  )
                })}
              </div>
            </div>
            <div className="ai-prompt flex flex-col items-start">
              <p className="mb-[16px] text-[18px] font-bold">속도</p>
              <div className="grid w-full grid-cols-1 gap-y-4 rounded-[10px] md:h-[40px] md:grid-cols-2">
                {tempoList?.map((tempo, index) => {
                  return (
                    <>
                      <div className="flex" key={index}>
                        <Controller
                          render={({ field }) => (
                            <>
                              <SpeedPlayer
                                {...field}
                                id={index}
                                index={index}
                                playing={tempoPlaying}
                                handleFn={handleTempoPlayButtonClick}
                                checked={watch('tempoId') === tempo?.id}
                                onChange={() => {
                                  setValue('tempoId', tempo.id)
                                  setTempoName(tempo?.name)
                                }}
                                label={tempo.name}
                                className="w-full text-left"
                                src={tempo.path}
                                tempoPlaying={tempoPlaying}
                                setTempoPlaying={setTempoPlaying}
                                tempoRefs={tempoRefs}
                              />{' '}
                            </>
                          )}
                          rules={{
                            validate: (value) => {
                              return Number(watch('aiPromptTypeId')) === 0
                                ? 'tempo ID must not be 0'
                                : true
                            },
                          }}
                          name="tempoId"
                          control={control}
                        />
                        {new Array(tempo?.cashRate).fill(0).map((el, i) => (
                          <>
                            {' '}
                            <img
                              src="assets/img/cash.png"
                              alt="cash"
                              className="ml-[2px] mt-[2px] h-[13px] w-[11px]"
                              key={`tempo_cash_${i}`}
                            />
                          </>
                        ))}
                        <div className="flex gap-[12px]">
                          <button type="button">
                            <audio
                              ref={(ref) => {
                                tempoRefs.current[index] = ref
                              }}
                            >
                              <source src={tempo.path} type="audio/mpeg" />
                            </audio>
                          </button>
                        </div>
                      </div>
                    </>
                  )
                })}
              </div>
            </div>
            <div className="flex flex-col gap-4">
              <hr className="w-full border-[1px] border-gray-300" />
              <UserVoiceList
                setValue={setValue}
                watch={watch}
                control={control}
                setVoiceName={setVoiceName}
                userVoiceList={userVoiceList}
                userVoiceRefetch={userVoiceRefetch}
                userInfo={userInfo}
              />
              <hr className="w-full border-[1px] border-gray-300" />
            </div>

            <div className="ai-prompt flex flex-col items-start md:min-h-[300px]">
              <p className="text-[24px] font-bold md:mb-[16px]">
                AI 기본 목소리{' '}
              </p>
              <div className="mb-4">
                <p className="text-left text-[14px] text-error"> </p>
                <div
                  className={classNames('', {
                    'flex gap-8': !isMobile,
                    'grid grid-cols-2': isMobile,
                  })}
                >
                  {[
                    { cashRate: 1, price: 500, originPrice: 100 },
                    { cashRate: 2, price: 1000, originPrice: 300 },
                    { cashRate: 3, price: 1500, originPrice: 500 },
                    { cashRate: 4, price: 2000, originPrice: 600 },
                  ].map((el) => (
                    <>
                      <span className="flex items-center gap-1 text-main">
                        {el.cashRate &&
                          new Array(el.cashRate)
                            .fill(1)
                            .map((el) => (
                              <img
                                src="assets/img/cash.png"
                                alt="check_circle"
                                className="h-[13px] w-[11px]"
                              />
                            ))}
                        <s className="text-[#5E6068]">
                          {el.price.toLocaleString()}
                        </s>{' '}
                        {el.originPrice} 캐시
                      </span>
                    </>
                  ))}
                </div>
              </div>
              {isMobile && (
                <p className="mb-4 text-left text-main">
                  {selectedPrompt.introduction}
                </p>
              )}
              <div
                className={classNames('', {
                  'select_box mb-[16px] flex gap-4': !isMobile,
                  'select_box mb-[16px] flex flex-col gap-4': isMobile,
                })}
              >
                <div className="flex items-center gap-2">
                  <span>성별</span>{' '}
                  <div
                    className="relative"
                    onMouseLeave={() => {
                      setGenderSelectBoxOpen(false)
                    }}
                  >
                    <button
                      className={classNames('', {
                        'h-[33px] w-[140px] rounded-[6px] border-[1px] border-[#D4D9DF] px-4 py-1 text-left text-[14px] font-[500]':
                          !isMobile,
                        'h-[51px] w-[140px] rounded-[6px] border-[1px] border-[#D4D9DF] px-4 py-1 text-left font-[500]':
                          isMobile,
                      })}
                      value={genderValue}
                      type="button"
                      onClick={() => {
                        setGenderSelectBoxOpen((prev) => !prev)
                      }}
                    >
                      {' '}
                      {genderWords.length > 0 ? genderWords.join(', ') : '성별'}
                    </button>
                    <img
                      src="assets/img/chevron-down.png"
                      className={classNames('', {
                        'absolute bottom-2 right-2 h-[16px] w-[16px]':
                          !isMobile,
                        'absolute bottom-4 right-2 h-[16px] w-[16px]': isMobile,
                      })}
                      alt=""
                      onClick={() => {
                        setGenderSelectBoxOpen((prev) => !prev)
                      }}
                    />
                    {isGenderSelectBoxOpen && (
                      <div className="absolute z-50 flex h-[110px] w-[120px] flex-col justify-center gap-2 rounded-[10px] bg-white px-3 py-1 shadow-main">
                        {voiceTags?.genders.map((gender, index) => {
                          return (
                            <>
                              <div
                                className="flex items-center gap-2"
                                key={`voice_tag_${index}`}
                              >
                                <input
                                  id={`gender-${index}`} // 고유한 id
                                  type="checkbox"
                                  className="h-[20px] w-[20px]"
                                  value={gender}
                                  checked={genderWords.includes(gender)}
                                  onChange={() => handleGenderChange(gender)}
                                />
                                <label htmlFor={`gender-${index}`}>
                                  {gender}
                                </label>
                              </div>
                            </>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div
                  className={classNames('flex items-center gap-2', {
                    '': !isMobile,
                  })}
                >
                  <span>연령</span>{' '}
                  <div
                    className="relative"
                    onMouseLeave={() => {
                      setAgeSelectBoxOpen(false)
                    }}
                  >
                    <button
                      className={classNames('', {
                        'h-[33px] w-[179px] rounded-[6px] border-[1px] border-[#D4D9DF] px-4 py-1 text-left text-[14px] font-[500]':
                          !isMobile,
                        'h-[51px] w-[197px] rounded-[6px] border-[1px] border-[#D4D9DF] px-4 py-1 text-left font-[500]':
                          isMobile,
                      })}
                      value={ageValue}
                      type="button"
                      onClick={() => {
                        setAgeSelectBoxOpen((prev) => !prev)
                      }}
                    >
                      {' '}
                      {ageWords.length > 0 ? ageWords.join(', ') : '연령'}
                    </button>
                    <img
                      src="assets/img/chevron-down.png"
                      className={classNames('', {
                        'absolute bottom-2 right-2 h-[16px] w-[16px]':
                          !isMobile,
                        'absolute bottom-4 right-2 h-[16px] w-[16px]': isMobile,
                      })}
                      alt=""
                      onClick={() => {
                        setAgeSelectBoxOpen((prev) => !prev)
                      }}
                    />{' '}
                    {isAgeSelectBoxOpen && (
                      <div className="absolute right-0 z-50 flex h-[140px] w-[120px] flex-col justify-center gap-2 rounded-[10px] bg-white px-3 py-1 shadow-main">
                        {voiceTags?.ages.map((age, index) => {
                          return (
                            <label
                              className="flex w-full items-center justify-start gap-2"
                              key={`voiceTag_ages_${index}`}
                            >
                              <input
                                id={`age-${index}`} // 고유한 id
                                type="checkbox"
                                className="h-[20px] w-[20px]"
                                value={age}
                                checked={ageWords.includes(age)}
                                onChange={() => handleAgeChange(age)}
                              />
                              <span>{age}</span>
                            </label>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-x-1 gap-y-2 md:grid-cols-3 md:pb-4">
                {voiceListValue?.map((voice, index) => {
                  return (
                    <>
                      <Controller
                        render={({ field }) => (
                          <TypeCastVoicePlayer
                            {...field}
                            id={index}
                            index={index}
                            playing={voicePlaying}
                            handleFn={handleVoicePlayButtonClick}
                            checked={watch('voiceId') === voice.id}
                            onChange={() => {
                              setValue('voiceId', voice.id)
                              setValue('userVoiceId', 0)
                              setVoiceName(voice.name)
                            }}
                            label={voice.name}
                            voiceRefs={voiceRefs}
                            src={voice.shortPath}
                            cashRate={voice?.cashRate}
                            imagePath={voice.image ?? ''}
                            service={voice?.service}
                          />
                        )}
                        name="voiceId"
                        control={control}
                      />
                    </>
                  )
                })}
              </div>
            </div>
            {isMobile && (
              <div
                className={classNames(
                  'z-50 flex w-full flex-col gap-8 bg-white',
                  {},
                )}
              >
                <button
                  className="z-50 h-[60px] w-full rounded-full bg-main font-bold text-white"
                  onClick={handleListModalOpen}
                  type="button"
                >
                  저장
                </button>
              </div>
            )}
          </div>
        </div>
        {!isMobile && (
          <div className="sticky bottom-0 z-50 w-full rounded-b-[20px] bg-white p-8 shadow-main before:absolute before:left-0 before:top-[-10px] before:h-[10px] before:w-full before:bg-gradient-to-t before:from-black/10 before:to-transparent before:content-[''] md:w-[816px]">
            <button
              className="z-50 h-[60px] w-full rounded-full bg-main font-bold text-white"
              onClick={handleListModalOpen}
              type="button"
            >
              저장
            </button>
          </div>
        )}
      </SelectionModal>
    </>
  )
}
